import React from 'react';
import { Archive, ExternalLink, Package2, X } from 'lucide-react';

import LoadingOverlay from '@/Modules/Core/Components/Animation/LoadingOverlay';
import { Button } from '@/Modules/Core/Components/shadcn/button';
import { SplitButton } from '@/Modules/Core/Components/shadcn/split-button';
import {
  Dialog,
  DialogContent,
} from '@/Modules/Core/Components/shadcn/dialog';
import { getBugData } from '../../Services/SearchFilesService';

const FETCH_BATCH_SIZE = 100;
const MAX_FETCH_PAGES = 20;

const BugsDialogSkeleton = ({ count = 4 }) => (
  <div className="space-y-3 px-5 pb-5" aria-hidden="true">
    {Array.from({ length: count }).map((_, index) => (
      <div
        key={`bug-dialog-skeleton-${index + 1}`}
        className="rounded-2xl border border-border/35 bg-background/40 p-4"
      >
        <div className="flex animate-pulse flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex items-center gap-2">
              <div className="h-4 w-28 rounded-full bg-muted/70" />
              <div className="h-5 w-16 rounded-full bg-muted/50" />
            </div>
            <div className="space-y-2">
              <div className="h-3 w-full rounded-full bg-muted/55" />
              <div className="h-3 w-4/5 rounded-full bg-muted/45" />
            </div>
          </div>
          <div className="h-9 w-24 rounded-xl bg-muted/55" />
        </div>
      </div>
    ))}
  </div>
);

const getBugDescription = (bug) =>
  bug?.description?.content?.[0]?.content?.[0]?.text ||
  bug?.description ||
  'No description provided.';

const normalizeBugPlatform = (bug) => {
  const normalized = String(
    bug?.platform ?? bug?.bug_platform ?? bug?.package_type ?? ''
  )
    .trim()
    .toUpperCase();

  return normalized === 'TAR' ? 'TAR' : 'ZIP';
};

const getPlatformType = (platform) => (platform === 'TAR' ? 'l' : 'w');

const getDownloadAllPlatform = (bugs) => {
  if (!Array.isArray(bugs) || bugs.length === 0) {
    return 'ZIP';
  }

  return bugs.every((bug) => normalizeBugPlatform(bug) === 'TAR')
    ? 'TAR'
    : 'ZIP';
};

const DOWNLOAD_OPTIONS = [
  {
    id: 'tar',
    label: 'Download TAR',
    rolloutLabel: 'Download Rollout TAR',
    shortLabel: 'TAR',
    platformLabel: 'TAR',
    platformType: 'l',
    icon: Archive,
  },
  {
    id: 'zip',
    label: 'Download ZIP',
    rolloutLabel: 'Download Rollout ZIP',
    shortLabel: 'ZIP',
    platformLabel: 'ZIP',
    platformType: 'w',
    icon: Package2,
  },
];

const getPreferredDownloadOption = (preferredPlatformType, fallbackPlatform) => {
  if (preferredPlatformType) {
    const configuredOption = DOWNLOAD_OPTIONS.find(
      (option) => option.platformType === preferredPlatformType
    );

    if (configuredOption) {
      return configuredOption;
    }
  }

  return (
    DOWNLOAD_OPTIONS.find(
      (option) => option.platformLabel === fallbackPlatform
    ) ?? DOWNLOAD_OPTIONS[0]
  );
};

const RolloutBugsDialog = ({
  open,
  onOpenChange,
  rolloutName,
  onDownloadAllBugs,
  onDownloadBug,
  preferredPlatformType,
  canDownloadRollouts = true,
}) => {
  const [bugs, setBugs] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const panelRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onOpenChange(false);
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) {
        return;
      }

      const focusableElements = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.setTimeout(() => {
      panelRef.current?.focus();
    }, 0);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onOpenChange, open]);

  React.useEffect(() => {
    if (!open || !rolloutName) {
      return;
    }

    let cancelled = false;

    const loadBugs = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        let page = 1;
        let total = 0;
        let aggregatedBugs = [];
        let hasMore = true;

        while (hasMore && page <= MAX_FETCH_PAGES) {
          const response = await getBugData({
            limit: FETCH_BATCH_SIZE,
            offset: page,
            filter: [],
            sort: [{ field: 'bug_id', dir: 'asc' }],
            rolloutName,
          });

          if (response?.isSuccess === false && !response?.bugsData) {
            throw new Error(response?.message || 'Failed to load rollout bugs.');
          }

          const pageItems = Array.isArray(response?.bugsData)
            ? response.bugsData
            : [];

          aggregatedBugs = aggregatedBugs.concat(pageItems);

          const totalFromApi = Number(
            response?.TotalCount ?? aggregatedBugs.length
          );
          total = Number.isFinite(totalFromApi)
            ? totalFromApi
            : aggregatedBugs.length;

          hasMore =
            pageItems.length === FETCH_BATCH_SIZE &&
            aggregatedBugs.length < total;
          page += 1;
        }

        if (!cancelled) {
          setBugs(aggregatedBugs);
        }
      } catch (error) {
        if (!cancelled) {
          setBugs([]);
          setErrorMessage(error?.message || 'Unable to load rollout bugs.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadBugs();

    return () => {
      cancelled = true;
    };
  }, [open, rolloutName]);

  const downloadAllPlatform = React.useMemo(
    () => getDownloadAllPlatform(bugs),
    [bugs]
  );
  const preferredDownloadOption = React.useMemo(() => {
    return getPreferredDownloadOption(
      preferredPlatformType,
      downloadAllPlatform
    );
  }, [downloadAllPlatform, preferredPlatformType]);
  const alternateDownloadOptions = React.useMemo(
    () =>
      DOWNLOAD_OPTIONS.filter(
        (option) => option.platformType !== preferredDownloadOption?.platformType
      ),
    [preferredDownloadOption]
  );
  const bugCountLabel = `${bugs.length} ${bugs.length === 1 ? 'bug' : 'bugs'}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[920px] gap-0 overflow-hidden rounded-2xl border border-border/40 bg-card/95 p-0 shadow-2xl">
        <div
          ref={panelRef}
          className="relative flex min-h-[560px] max-h-[min(86vh,860px)] flex-col outline-none"
          tabIndex={-1}
        >
          <div className="shrink-0 border-b border-border/35 px-5 py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-1.5">
                <p className="text-[15px] font-semibold text-foreground">
                  Rollout Bugs
                </p>
                <p className="truncate font-mono text-[12px] text-muted-foreground">
                  {rolloutName}
                </p>
              </div>

              <div className="flex items-center gap-2">
                {canDownloadRollouts ? (
                  <div className="w-full max-w-[220px] sm:w-auto">
                    <SplitButton
                      primaryAction={
                        <>
                          {preferredDownloadOption?.platformType === 'l' ? (
                            <Archive className="mr-2 h-[13px] w-[13px]" strokeWidth={1.4} />
                          ) : (
                            <Package2 className="mr-2 h-[13px] w-[13px]" strokeWidth={1.4} />
                          )}
                          <span className="truncate">
                            {preferredDownloadOption?.rolloutLabel ?? 'Download Rollout'}
                          </span>
                        </>
                      }
                      menuOptions={alternateDownloadOptions}
                      disabled={loading || bugs.length === 0}
                      onPrimaryClick={() =>
                        onDownloadAllBugs?.(
                          bugs.map((bug) => bug?.bug_id).filter(Boolean),
                          preferredDownloadOption?.platformType ??
                            getPlatformType(downloadAllPlatform)
                        )
                      }
                      onSelect={(option) =>
                        onDownloadAllBugs?.(
                          bugs.map((bug) => bug?.bug_id).filter(Boolean),
                          option.platformType
                        )
                      }
                      className="w-full"
                      primaryClassName="px-3.5"
                      triggerClassName="w-9"
                      menuClassName="min-w-[190px]"
                    />
                  </div>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 rounded-xl p-0 text-muted-foreground hover:bg-accent/20 hover:text-foreground"
                  aria-label="Close bugs panel"
                  title="Close"
                  onClick={() => onOpenChange(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {loading && bugs.length === 0 ? (
            <div className="flex min-h-0 flex-1 flex-col pt-4">
              <div className="shrink-0 px-5 pb-3">
                <div className="h-3 w-24 animate-pulse rounded-full bg-muted/60" />
              </div>
              <BugsDialogSkeleton />
            </div>
          ) : errorMessage ? (
            <div className="px-5 py-5">
              <div className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-[13px] text-destructive">
                {errorMessage}
              </div>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="shrink-0 px-5 pb-3 pt-4">
                <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                  {bugCountLabel}
                </p>
              </div>

              {bugs.length === 0 ? (
                <div className="flex min-h-[180px] items-center justify-center px-5 pb-6 pt-2 text-center text-[13px] text-muted-foreground">
                  No bugs found.
                </div>
              ) : (
                <div className="relative flex-1">
                  <LoadingOverlay
                    loading={loading}
                    label="Refreshing bugs"
                    className="mx-5 mb-5 mt-0 rounded-2xl"
                  />
                  <div className="themed-slim-scrollbar h-full overflow-y-auto px-5 pb-5">
                    <div className="space-y-3">
                      {bugs.map((bug) => {
                        const bugPlatform = normalizeBugPlatform(bug);
                        const bugPreferredDownloadOption =
                          getPreferredDownloadOption(
                            preferredPlatformType,
                            bugPlatform
                          );
                        const bugAlternateDownloadOptions =
                          DOWNLOAD_OPTIONS.filter(
                            (option) =>
                              option.platformType !==
                              bugPreferredDownloadOption.platformType
                          );

                        return (
                          <article
                            key={bug?.bug_id ?? bug?.id}
                            className="rounded-2xl border border-border/35 bg-background/40 p-4 transition-[border-color,background-color] duration-120 hover:border-border/60 hover:bg-background/55"
                          >
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                              <div className="min-w-0 space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  {bug?.bug_url ? (
                                    <a
                                      href={bug.bug_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-foreground transition-colors duration-100 hover:text-primary"
                                    >
                                      <span>{bug.bug_id}</span>
                                      <ExternalLink
                                        className="h-[11px] w-[11px] text-primary"
                                        strokeWidth={1.4}
                                      />
                                    </a>
                                  ) : (
                                    <span className="text-[14px] font-semibold text-foreground">
                                      {bug?.bug_id}
                                    </span>
                                  )}

                                  {bug?.status ? (
                                    <span className="inline-flex rounded-full border border-border/45 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                                      {bug.status}
                                    </span>
                                  ) : null}
                                </div>

                                <p className="text-[12px] leading-5 text-muted-foreground">
                                  {getBugDescription(bug)}
                                </p>
                              </div>

                              {canDownloadRollouts ? (
                                <div className="shrink-0">
                                  <div className="w-full min-w-[132px] sm:w-auto">
                                    <SplitButton
                                      primaryAction={
                                        <>
                                          {bugPreferredDownloadOption.platformType === 'l' ? (
                                            <Archive
                                              className="mr-2 h-[13px] w-[13px]"
                                              strokeWidth={1.4}
                                            />
                                          ) : (
                                            <Package2
                                              className="mr-2 h-[13px] w-[13px]"
                                              strokeWidth={1.4}
                                            />
                                          )}
                                          <span className="truncate">
                                            {bugPreferredDownloadOption.shortLabel}
                                          </span>
                                        </>
                                      }
                                      menuOptions={bugAlternateDownloadOptions}
                                      disabled={!bug?.bug_id}
                                      onPrimaryClick={() =>
                                        onDownloadBug?.(
                                          bug?.bug_id,
                                          bugPreferredDownloadOption.platformType
                                        )
                                      }
                                      onSelect={(option) =>
                                        onDownloadBug?.(bug?.bug_id, option.platformType)
                                      }
                                      className="w-full"
                                      primaryClassName="px-3"
                                      triggerClassName="w-9"
                                      menuClassName="min-w-[170px]"
                                    />
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RolloutBugsDialog;
